import React, { useEffect, useState, useCallback, useRef } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle, faRotate,
  faArrowRight, faHashtag, faBan, faPercent, faCoins,
  faChevronDown, faClipboardList,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';
import dayjs from 'dayjs';

interface MismatchedOrder {
  id: string;
  order_number: string;
  reference_id: string;
  customer_name: string;
  customer_email: string;
  total_amount: number;
  base_amount: number;
  bank_number: string;
  bank_name: string;
  merchant_id: string;
  status: string;
  created_at: string;
  merchant?: { id: string; name: string; email: string };
  bank?: { id: string; name: string; bank_code: string };
  transaction_data?: {
    from_account: string;
    amount: number;
    transaction_date: string;
    sender: string;
  };
  sender_account_suffix: string;
  suggested_correct_orders: {
    id: string;
    order_number: string;
    reference_id: string;
    bank_number: string;
    bank_name: string;
    total_amount: number;
    customer_name: string;
    status: string;
    created_at: string;
  }[];
}

interface Summary {
  total_mismatched: number;
  total_mismatched_amount: number;
  total_paid_orders: number;
  mismatch_rate: number;
}

const MismatchedAccountsPage: React.FC = () => {
  const [data, setData] = useState<MismatchedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<Summary>({
    total_mismatched: 0, total_mismatched_amount: 0, total_paid_orders: 0, mismatch_rate: 0
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  // Date filter
  const today = dayjs().format('YYYY-MM-DD');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [appliedDateFrom, setAppliedDateFrom] = useState(today);
  const [appliedDateTo, setAppliedDateTo] = useState(today);

  // Expanded row
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  const loadData = useCallback(async (showLoading = true) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      if (showLoading) setLoading(true);
      const offset = (currentPage - 1) * pageSize;
      const res = await merchantApi.get(
        `/admin/orders/mismatched-accounts?limit=${pageSize}&offset=${offset}&date_from=${appliedDateFrom}&date_to=${appliedDateTo}`,
        { signal: controller.signal }
      );
      if (!controller.signal.aborted) {
        setData(res.data.data || []);
        setSummary(res.data.summary || { total_mismatched: 0, total_mismatched_amount: 0, total_paid_orders: 0, mismatch_rate: 0 });
        setTotalItems(res.data.pagination?.total || 0);
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError' && error?.code !== 'ERR_CANCELED') {
        toast.error('ไม่สามารถโหลดข้อมูลได้');
      }
    } finally {
      if (!controller.signal.aborted && showLoading) setLoading(false);
    }
  }, [currentPage, pageSize, appliedDateFrom, appliedDateTo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh 5 นาที
  useEffect(() => {
    const interval = setInterval(() => loadData(false), 300000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleDateSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    const t = dayjs().format('YYYY-MM-DD');
    setDateFrom(t);
    setDateTo(t);
    setAppliedDateFrom(t);
    setAppliedDateTo(t);
    setCurrentPage(1);
  };

  const formatAmount = (amount: number | string) =>
    parseFloat(String(amount || 0)).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const statCards: StatCardDef[] = [
    {
      icon: faBan,
      iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'จำนวนที่ไม่ตรง',
      value: <span className="text-red-600 dark:text-red-400">{summary.total_mismatched.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">orders</span>,
    },
    {
      icon: faCoins,
      iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      label: 'ยอดรวมไม่ตรง',
      value: <span className="text-orange-600 dark:text-orange-400">฿{formatAmount(summary.total_mismatched_amount)}</span>,
    },
    {
      icon: faHashtag,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'Paid Orders ทั้งหมด',
      value: <span className="text-blue-600 dark:text-blue-400">{summary.total_paid_orders.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ในช่วงวันที่เลือก</span>,
    },
    {
      icon: faPercent,
      iconClassName: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      label: 'อัตราไม่ตรง',
      value: <span className="text-purple-600 dark:text-purple-400">{summary.mismatch_rate.toFixed(1)}%</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">จาก paid orders</span>,
    },
  ];

  const columns: Column<MismatchedOrder>[] = [
    {
      key: 'order_info',
      header: 'Order (ถูกจับคู่)',
      width: '220px',
      render: (item) => (
        <div>
          <p className="font-bold text-gray-800 dark:text-slate-100 text-sm">{item.order_number}</p>
          {item.reference_id && (
            <p className="text-[10px] text-gray-400 dark:text-slate-500 truncate max-w-[200px]">Ref: {item.reference_id}</p>
          )}
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{item.customer_name || item.customer_email || '-'}</p>
        </div>
      ),
    },
    {
      key: 'merchant',
      header: 'Merchant',
      width: '140px',
      render: (item) => (
        <div>
          <p className="font-medium text-gray-700 dark:text-slate-200 text-sm">{item.merchant?.name || '-'}</p>
          <p className="text-[10px] text-gray-400 dark:text-slate-500">{item.merchant?.email || ''}</p>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'ยอดเงิน',
      width: '100px',
      render: (item) => (
        <span className="font-bold text-sm" style={{ color: 'var(--theme-accent)' }}>
          ฿{formatAmount(item.total_amount)}
        </span>
      ),
    },
    {
      key: 'order_bank',
      header: 'บัญชีใน Order',
      width: '150px',
      render: (item) => (
        <div>
          <p className="text-xs font-semibold text-gray-700 dark:text-slate-200">{item.bank_name || item.bank?.name || '-'}</p>
          <p className="text-xs text-gray-500 dark:text-slate-400 font-mono">{item.bank_number || '-'}</p>
        </div>
      ),
    },
    {
      key: 'tx_account',
      header: 'บัญชีผู้โอน (TX)',
      width: '180px',
      render: (item) => (
        <div>
          <span className="inline-block px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-xs font-bold font-mono">
            ...{item.sender_account_suffix}
          </span>
          <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1 truncate max-w-[170px]" title={item.transaction_data?.from_account}>
            {item.transaction_data?.from_account || '-'}
          </p>
        </div>
      ),
    },
    {
      key: 'suggested',
      header: 'Order ที่น่าจะถูกต้อง',
      width: '220px',
      render: (item) => {
        const suggestions = item.suggested_correct_orders || [];
        if (suggestions.length === 0) {
          return <span className="text-gray-400 dark:text-slate-500 text-xs italic">ไม่พบ order ที่ตรง</span>;
        }
        return (
          <div>
            {suggestions.slice(0, 2).map((s) => (
              <div key={s.id} className="flex items-center gap-1.5 text-xs mb-1 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-lg">
                <FontAwesomeIcon icon={faArrowRight} className="text-green-500 text-[10px]" />
                <span className="font-bold text-green-700 dark:text-green-300">{s.order_number}</span>
                <span className="text-gray-400 dark:text-slate-500 font-mono text-[10px]">({s.bank_number})</span>
              </div>
            ))}
            {suggestions.length > 2 && (
              <button
                onClick={(e) => { e.stopPropagation(); setExpandedRowId(item.id); }}
                className="text-[10px] text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 cursor-pointer mt-0.5"
              >
                +{suggestions.length - 2} รายการ
                <FontAwesomeIcon icon={faChevronDown} className="ml-1" />
              </button>
            )}
          </div>
        );
      },
    },
    {
      key: 'created_at',
      header: 'วันที่',
      width: '110px',
      sortable: true,
      render: (item) => (
        <span className="text-gray-600 dark:text-slate-300 text-xs">
          {dayjs(item.created_at).format('DD/MM/YY HH:mm')}
        </span>
      ),
    },
  ];

  const expandedItem = expandedRowId ? data.find(d => d.id === expandedRowId) : null;

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faExclamationTriangle}
        title="บัญชีไม่ตรง"
        subtitle={`Orders ที่เลขบัญชีผู้โอนไม่ตรงกับบัญชีของ Order • ${totalItems.toLocaleString()} รายการ`}
      />

      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleDateSearch}
        onReset={handleFilterReset}
        loading={loading}
        actions={
          <UiButton variant="secondary" icon={faRotate} onClick={() => loadData()} loading={loading}>
            Refresh
          </UiButton>
        }
      />

      <UiStatsGrid cards={statCards} loading={loading} cols={4} />

      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        emptyMessage="ไม่พบ orders ที่บัญชีไม่ตรงในช่วงวันที่เลือก"
        emptyIcon={<FontAwesomeIcon icon={faClipboardList} className="text-5xl text-gray-200 dark:text-slate-600" />}
        keyExtractor={(item) => item.id}
        hoverable
        compact
        serverPagination={{
          currentPage,
          totalItems,
          pageSize,
          onPageChange: setCurrentPage,
          onPageSizeChange: (size) => { setPageSize(size); setCurrentPage(1); },
        }}
      />

      {expandedItem && expandedItem.suggested_correct_orders?.length > 0 && (
        <UiModal
          open={!!expandedRowId}
          onClose={() => setExpandedRowId(null)}
          title="Order ที่น่าจะถูกต้อง"
          icon={faExclamationTriangle}
          iconColor="text-orange-500"
          size="lg"
          scrollable
        >
          <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 mb-4 border border-red-100 dark:border-red-800">
            <p className="text-xs text-red-600 dark:text-red-300 font-semibold mb-1">Transaction ถูกจับคู่กับ (ผิด):</p>
            <p className="text-sm font-bold text-gray-800 dark:text-slate-100">{expandedItem.order_number}</p>
            <p className="text-xs text-gray-500 dark:text-slate-400">บัญชี: {expandedItem.bank_name} {expandedItem.bank_number}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">ผู้โอนจาก TX: ...{expandedItem.sender_account_suffix}</p>
          </div>

          <p className="text-xs text-gray-500 dark:text-slate-400 mb-2 font-semibold">
            Orders ที่เลขบัญชีตรงกับผู้โอน ({expandedItem.suggested_correct_orders.length} รายการ):
          </p>
          <div className="space-y-2">
            {expandedItem.suggested_correct_orders.map((s) => (
              <div key={s.id} className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 flex-wrap">
                  <FontAwesomeIcon icon={faArrowRight} className="text-green-500" />
                  <span className="font-bold text-green-700 dark:text-green-300 text-sm">{s.order_number}</span>
                  <span className="text-xs font-bold" style={{ color: 'var(--theme-accent)' }}>
                    ฿{formatAmount(s.total_amount)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  บัญชี: {s.bank_name} <span className="font-mono">{s.bank_number}</span>
                </p>
                <p className="text-xs text-gray-400 dark:text-slate-500">
                  {s.customer_name || '-'} | {dayjs(s.created_at).format('DD/MM/YY HH:mm')}
                </p>
              </div>
            ))}
          </div>
        </UiModal>
      )}
    </div>
  );
};

export default MismatchedAccountsPage;
